import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Languages, Copy, ExternalLink, ArrowRightLeft, Check, Clipboard } from "lucide-react";

const TranslatePage = () => {
  const [sourceText, setSourceText] = useState("");
  const [translatedText, setTranslatedText] = useState("");
  const [sourceLanguage, setSourceLanguage] = useState("pt");
  const [targetLanguage, setTargetLanguage] = useState("en");
  const [recentTranslations, setRecentTranslations] = useState<{ source: string; target: string; text: string; translation: string }[]>([]);

  const languages = [
    { value: "pt", label: "Português" },
    { value: "en", label: "English" },
    { value: "es", label: "Español" },
    { value: "fr", label: "Français" },
    { value: "de", label: "Deutsch" },
    { value: "it", label: "Italiano" },
    { value: "ja", label: "日本語" },
    { value: "ko", label: "한국어" },
    { value: "zh", label: "中文" },
    { value: "ru", label: "Русский" },
    { value: "ar", label: "العربية" },
  ];

  const handleTranslate = () => {
    // Mock translation (in a real app, this would call an API)
    let mockTranslation = "";
    
    if (sourceText) {
      if (sourceLanguage === "pt" && targetLanguage === "en") {
        mockTranslation = `This is a simulated English translation of your Portuguese text. The original was: "${sourceText}"`;
      } else if (sourceLanguage === "en" && targetLanguage === "pt") {
        mockTranslation = `Esta é uma tradução simulada para português do seu texto em inglês. O original foi: "${sourceText}"`;
      } else if (sourceLanguage === "es" && targetLanguage === "en") {
        mockTranslation = `This is a simulated English translation of your Spanish text. The original was: "${sourceText}"`;
      } else {
        mockTranslation = `This is a simulated translation from ${sourceLanguage} to ${targetLanguage}. The original text was: "${sourceText}"`;
      }
    }
    
    setTranslatedText(mockTranslation);
    
    // Add to recent translations
    if (sourceText && mockTranslation) {
      setRecentTranslations(prev => [
        { 
          source: sourceLanguage, 
          target: targetLanguage, 
          text: sourceText, 
          translation: mockTranslation 
        },
        ...prev.slice(0, 4) // Keep only the 5 most recent translations
      ]);
    }
    
    toast.success("Texto traduzido com sucesso!");
  };

  const handleCopyText = () => {
    navigator.clipboard.writeText(translatedText);
    toast.success("Texto copiado para a área de transferência!");
  };

  const handleSwapLanguages = () => {
    const temp = sourceLanguage;
    setSourceLanguage(targetLanguage);
    setTargetLanguage(temp);
    
    // Also swap the text
    setSourceText(translatedText);
    setTranslatedText(sourceText);
  };

  const openGoogleTranslate = () => {
    const url = `https://translate.google.com.br/?sl=${sourceLanguage}&tl=${targetLanguage}&op=translate`;
    window.open(url, "_blank");
  };

  const useRecentTranslation = (index: number) => {
    const item = recentTranslations[index];
    setSourceLanguage(item.source);
    setTargetLanguage(item.target);
    setSourceText(item.text);
    setTranslatedText(item.translation);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            Tradutor de Texto
          </h1>
          <p className="text-muted-foreground max-w-3xl mx-auto">
            Traduza textos entre diferentes idiomas para criar conteúdo para suas redes sociais, vídeos e blogs. Perfeito para uso com ferramentas como LipSync, YouTube, Pinterest e muito mais.
          </p>
        </div>

        <Tabs defaultValue="translate" className="max-w-5xl mx-auto">
          <TabsList className="grid grid-cols-3 mb-8">
            <TabsTrigger value="translate">Tradutor</TabsTrigger>
            <TabsTrigger value="history">Histórico</TabsTrigger>
            <TabsTrigger value="tools">Ferramentas</TabsTrigger>
          </TabsList>

          <TabsContent value="translate">
            <Card>
              <CardHeader>
                <CardTitle>Tradutor de Texto</CardTitle>
                <CardDescription>
                  Traduza textos entre diferentes idiomas para usar em seus projetos
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex flex-col md:flex-row gap-4 items-center">
                  <div className="w-full md:w-[40%]">
                    <Select value={sourceLanguage} onValueChange={setSourceLanguage}>
                      <SelectTrigger>
                        <SelectValue placeholder="Idioma de origem" />
                      </SelectTrigger>
                      <SelectContent>
                        {languages.map((lang) => (
                          <SelectItem key={lang.value} value={lang.value}>
                            {lang.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="rounded-full"
                    onClick={handleSwapLanguages}
                  >
                    <ArrowRightLeft className="h-4 w-4" />
                  </Button>
                  
                  <div className="w-full md:w-[40%]">
                    <Select value={targetLanguage} onValueChange={setTargetLanguage}>
                      <SelectTrigger>
                        <SelectValue placeholder="Idioma de destino" />
                      </SelectTrigger>
                      <SelectContent>
                        {languages.map((lang) => (
                          <SelectItem key={lang.value} value={lang.value}>
                            {lang.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">Texto original</h3>
                  <Textarea 
                    placeholder="Digite o texto que deseja traduzir"
                    rows={5}
                    value={sourceText}
                    onChange={(e) => setSourceText(e.target.value)}
                  />
                </div>
                
                <div className="flex justify-center">
                  <Button 
                    onClick={handleTranslate}
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    <Languages className="mr-2 h-4 w-4" /> Traduzir texto
                  </Button>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold">Texto traduzido</h3>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={handleCopyText}
                      disabled={!translatedText}
                    >
                      <Copy className="mr-2 h-4 w-4" /> Copiar
                    </Button>
                  </div>
                  <Textarea 
                    placeholder="Tradução aparecerá aqui"
                    rows={5}
                    value={translatedText}
                    onChange={(e) => setTranslatedText(e.target.value)}
                    readOnly
                  />
                </div>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button 
                  variant="outline"
                  onClick={openGoogleTranslate}
                >
                  <ExternalLink className="mr-2 h-4 w-4" /> Abrir no Google Tradutor
                </Button>
                <Button 
                  variant="ghost"
                  onClick={() => {
                    setSourceText("");
                    setTranslatedText("");
                  }}
                >
                  Limpar
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>

          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle>Histórico de Traduções</CardTitle>
                <CardDescription>
                  Suas traduções recentes
                </CardDescription>
              </CardHeader>
              <CardContent>
                {recentTranslations.length > 0 ? (
                  <div className="space-y-4">
                    {recentTranslations.map((item, index) => (
                      <Card key={index} className="overflow-hidden">
                        <CardHeader className="bg-muted/30 p-4">
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold">{languages.find(l => l.value === item.source)?.label || item.source}</span>
                              <ArrowRightLeft className="h-4 w-4 text-muted-foreground" />
                              <span className="font-semibold">{languages.find(l => l.value === item.target)?.label || item.target}</span>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => useRecentTranslation(index)}
                            >
                              <Check className="mr-2 h-4 w-4" /> Usar
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent className="p-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm text-muted-foreground mb-1">Original:</p>
                              <p className="text-sm break-words">{item.text.length > 100 ? `${item.text.substring(0, 100)}...` : item.text}</p>
                            </div>
                            <div>
                              <p className="text-sm text-muted-foreground mb-1">Tradução:</p>
                              <p className="text-sm break-words">{item.translation.length > 100 ? `${item.translation.substring(0, 100)}...` : item.translation}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">Nenhuma tradução recente.</p>
                    <p className="mt-2">Faça sua primeira tradução na aba "Tradutor".</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="tools">
            <Card>
              <CardHeader>
                <CardTitle>Ferramentas de Tradução</CardTitle>
                <CardDescription>
                  Links úteis para tradução e criação de conteúdo
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-xl">Google Tradutor</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground mb-4">
                        Ferramenta gratuita do Google para tradução de textos, documentos e websites entre mais de 100 idiomas.
                      </p>
                      <Button 
                        onClick={() => window.open("https://translate.google.com.br/?sl=es&tl=en&op=translate", "_blank")}
                        className="w-full"
                      >
                        <ExternalLink className="mr-2 h-4 w-4" /> Abrir Google Tradutor
                      </Button>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-xl">LipSync</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground mb-4">
                        Crie vídeos com personagens falantes a partir de texto. Use com texto traduzido para criar conteúdo em diferentes idiomas.
                      </p>
                      <Button 
                        onClick={() => window.open("https://lipsync.video/", "_blank")}
                        className="w-full"
                      >
                        <ExternalLink className="mr-2 h-4 w-4" /> Visitar LipSync
                      </Button>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-xl">Blogger</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground mb-4">
                        Plataforma de blogs do Google, perfeita para publicar conteúdo traduzido e vídeos criados com LipSync.
                      </p>
                      <div className="space-y-2">
                        <Button 
                          onClick={() => window.open("https://scriptjogobicho.blogspot.com/2025/08/videos-shorts-animais.html", "_blank")}
                          className="w-full"
                          variant="outline"
                        >
                          <ExternalLink className="mr-2 h-4 w-4" /> Vídeos Shorts Animais
                        </Button>
                        <Button 
                          onClick={() => window.open("https://scriptjogobicho.blogspot.com/2025/08/criando-audio-e-video-no-lipsync.html", "_blank")}
                          className="w-full"
                          variant="outline"
                        >
                          <ExternalLink className="mr-2 h-4 w-4" /> Tutorial LipSync
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-xl">Pinterest</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground mb-4">
                        Compartilhe seus vídeos e imagens traduzidos no Pinterest para alcançar uma audiência global.
                      </p>
                      <Button 
                        onClick={() => window.open("https://br.pinterest.com/crisgride/", "_blank")}
                        className="w-full"
                      >
                        <ExternalLink className="mr-2 h-4 w-4" /> Visitar Pinterest
                      </Button>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-xl">Canva</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground mb-4">
                        Crie designs com textos traduzidos para suas redes sociais, blogs e vídeos.
                      </p>
                      <Button 
                        onClick={() => window.open("https://www.canva.com/download/windows/", "_blank")}
                        className="w-full"
                      >
                        <ExternalLink className="mr-2 h-4 w-4" /> Baixar Canva para Windows
                      </Button>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-xl">Adobe Express</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground mb-4">
                        Crie animações e vídeos para o YouTube com textos traduzidos em diferentes idiomas.
                      </p>
                      <Button 
                        onClick={() => window.open("https://www.adobe.com/express/create/animation/youtube", "_blank")}
                        className="w-full"
                      >
                        <ExternalLink className="mr-2 h-4 w-4" /> Adobe Express para YouTube
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
      <Footer />
    </div>
  );
};

export default TranslatePage;